import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, onMounted, watch } from "vue";
import Multiselect from "vue-multiselect";
import { useMultiselect } from "@/composables/useMultiselect";
import { uid } from "@/utils/utils";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormSelect',
    props: {
        id: { type: String, default: `form-select-${uid()}` },
        disabled: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        optional: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: "Select Value",
        },
        value: {
            type: [String, Array],
            default: null,
        },
    },
    emits: ["input"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faCheckSquare, faSquare);
        const { ariaExpanded, onOpen, onClose } = useMultiselect();
        /**
         * When there are more options than this, push selected options to the end
         */
        const optionReorderThreshold = 8;
        const reorderedOptions = computed(() => {
            if (props.options.length <= optionReorderThreshold) {
                return props.options;
            }
            else {
                const selectedOptions = [];
                const unselectedOptions = [];
                props.options.forEach((option) => {
                    if (selectedValues.value.includes(option.value)) {
                        selectedOptions.push(option);
                    }
                    else {
                        unselectedOptions.push(option);
                    }
                });
                return [...unselectedOptions, ...selectedOptions];
            }
        });
        /**
         * Tracks if the select field has options
         */
        const hasOptions = computed(() => {
            return props.options.length > 0;
        });
        /**
         * Provides initial value if necessary
         */
        const initialValue = computed(() => {
            if (props.value === null && !props.optional && hasOptions.value) {
                const v = props.options[0];
                if (v) {
                    return v.value;
                }
            }
            return null;
        });
        /**
         * Configure selected label
         */
        const selectedLabel = computed(() => {
            return props.multiple ? "Selected" : "";
        });
        /**
         * Tracks selected values
         */
        const selectedValues = computed(() => (Array.isArray(props.value) ? props.value : [props.value]));
        /**
         * Tracks current value and emits changes
         */
        const currentValue = computed({
            get: () => props.options.filter((option) => selectedValues.value.includes(option.value)),
            set: (val) => {
                if (Array.isArray(val)) {
                    if (val.length > 0) {
                        const values = val.map((v) => v.value);
                        emit("input", values);
                    }
                    else {
                        emit("input", null);
                    }
                }
                else {
                    emit("input", val ? val.value : null);
                }
            },
        });
        /**
         * Ensures that an initial value is selected for non-optional inputs
         */
        function setInitialValue() {
            if (initialValue.value) {
                emit("input", initialValue.value);
            }
        }
        /**
         * Watches changes in select options and adjusts initial value if necessary
         */
        watch(() => props.options, () => setInitialValue());
        /**
         * Sets initial value if necessary
         */
        onMounted(() => {
            setInitialValue();
        });
        return { __sfc: true, ariaExpanded, onOpen, onClose, props, emit, optionReorderThreshold, reorderedOptions, hasOptions, initialValue, selectedLabel, selectedValues, currentValue, setInitialValue, faCheckSquare, faSquare, FontAwesomeIcon, Multiselect };
    }
});
